import React, { useEffect, useState, forwardRef } from 'react';
import { useLanguage, useTranslate } from '../../lib/i18n';
import MarkdownRenderer from '../MarkdownRenderer';
import InformationCards from '../InfoCardStackList';
import RoboticIcon from '../customIcons/RoboticIcon';
import ThinkingIndicator from '../ThinkingIndicator';
import InfoCardsSection from '../InfoCardsSection';
import { useInfoCardStack } from '../../hooks/queries/useInfoCards';

interface Props {
  content: string;
  time: number | string; // TODO: Why do we allow strings and numbers?
  special?: string;
  topic: string | null;
  showInfoCards: boolean;
  hideInfoCards: boolean;
  onHideInfoCards: () => void;
  infoCardStackId?: string;
  infoCardsContext?: string;
}

const AssistantOperatorMessage = forwardRef<HTMLDivElement, Props>(
  (
    {
      content,
      time,
      special,
      topic,
      showInfoCards,
      hideInfoCards,
      onHideInfoCards,
      infoCardStackId,
      infoCardsContext,
    },
    chatContainerRef
  ) => {
    const t = useTranslate();

    const [convertedTime, setConvertedTime] = useState('');
    const [isCardExpanded, setIsCardExpanded] = useState(false);

    const { language } = useLanguage();
    const { data: infoCardStack } = useInfoCardStack(language, infoCardStackId);

    const shouldRenderInfoCards = showInfoCards && !hideInfoCards && infoCardStack;

    useEffect(() => {
      if (!time) return;
      // Parse the input date string
      const date = new Date(time);

      // Get hours and minutes in local time
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      // Return the formatted time
      setConvertedTime(`${hours}:${minutes}`);
    }, [time]);

    return (
      <div ref={chatContainerRef} className="group relative flex w-full justify-start">
        <div className="flex animate-slide-up items-start">
          <div className="shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted border">
              <span className="text-xs text-foreground">
                <RoboticIcon size={18} />
              </span>
            </div>
          </div>
          <div className="mx-2 grow space-y-1">
            <div className="flex items-center space-x-2 mb-1">
              {content !== t('common.labels.writing') && (
                <>
                  <span className="text-xs font-semibold text-muted-foreground">
                    {t('operatorAssistantMessage.botName')}
                  </span>
                  <span className="text-xs text-muted-foreground">{convertedTime}</span>
                </>
              )}
            </div>
            <div className="text-sm">
              {special ? (
                <>
                  {special === 'moreInfo' && topic && infoCardStack && (
                    <>
                      <p>
                        {t('assistantMessage.infoAboutTopic', {
                          topic: t('topics.' + topic.toLowerCase()),
                        })}
                      </p>
                      <InformationCards stacks={[infoCardStack]} />
                    </>
                  )}
                </>
              ) : content === t('common.labels.writing') ? (
                <ThinkingIndicator />
              ) : (
                <div className="inline-block max-w-full text-left text-sm text-foreground">
                  <div className="overflow-wrap-anywhere whitespace-pre-wrap break-words">
                    <MarkdownRenderer markdown={content} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mx-2 grow space-y-1">
          {shouldRenderInfoCards && (
            <InfoCardsSection
              infoCardsContext={infoCardsContext}
              topic={topic!}
              infoCardStack={infoCardStack}
              isCardExpanded={isCardExpanded}
              onToggle={() => setIsCardExpanded(!isCardExpanded)}
              onHideInfoCards={onHideInfoCards}
              chatContainerRef={chatContainerRef}
            />
          )}
        </div>
      </div>
    );
  }
);

AssistantOperatorMessage.displayName = 'AssistantOperatorMessage';

export default AssistantOperatorMessage;
